import { gathrxrAuthHeader } from './quotaHeaders';

// Every browser upload to the R2 bucket goes through here. The browser holds
// no storage credentials: it asks /api/storage/presign (signed-in users only)
// for a short-lived presigned PUT for one key, then sends the bytes straight
// to R2. The server checks the prefix, content type, size and ownership.

export interface PutObjectOptions {
  cacheControl?: string;
  onProgress?: (pct: number) => void;
  signal?: AbortSignal;
}

/** Keys may only hold [A-Za-z0-9._-/]; anything else becomes '-'. */
export function sanitizeStorageKey(key: string): string {
  return key
    .replace(/[^A-Za-z0-9._\-/]/g, '-')
    .replace(/\/{2,}/g, '/')
    .replace(/\.{2,}/g, '.')
    .replace(/^[^A-Za-z0-9]+/, '');
}

/** Upload one object and return its public URL. */
export async function putObject(
  key: string,
  data: Blob | Uint8Array | ArrayBuffer,
  contentType: string,
  opts: PutObjectOptions = {},
): Promise<string> {
  const blob = data instanceof Blob ? data : new Blob([data as BlobPart]);
  const type = (contentType || blob.type || 'application/octet-stream').split(';')[0].trim();
  const res = await fetch('/api/storage/presign', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...(await gathrxrAuthHeader()) },
    body: JSON.stringify({ key: sanitizeStorageKey(key), contentType: type, size: blob.size, cacheControl: opts.cacheControl }),
    signal: opts.signal,
  });
  const info: any = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(info?.error || `Upload not allowed (${res.status})`);

  await new Promise<void>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', info.uploadUrl);
    for (const [k, v] of Object.entries(info.headers || {})) xhr.setRequestHeader(k, String(v));
    xhr.upload.onprogress = (e) => { if (e.lengthComputable) opts.onProgress?.(Math.round((e.loaded / e.total) * 100)); };
    xhr.onload = () => (xhr.status >= 200 && xhr.status < 300 ? resolve() : reject(new Error(`Upload failed (${xhr.status})`)));
    xhr.onerror = () => reject(new Error('Upload failed (network)'));
    xhr.onabort = () => reject(new DOMException('Upload aborted', 'AbortError'));
    opts.signal?.addEventListener('abort', () => xhr.abort(), { once: true });
    xhr.send(blob);
  });
  opts.onProgress?.(100);
  return info.url as string;
}

// Upload raw bytes to the bucket and return the public URL. Shared by the
// avatar creator, the wardrobe thumbnail backfill and the AI services.
export async function uploadBytesToStorage(data: Blob | Uint8Array, key: string, contentType: string): Promise<string> {
  return putObject(key, data, contentType);
}
